import { api, requestStream } from './client';
import type { CanvasContextSnapshot, ChatSessionResponse } from '../types';

export interface ToolStartPayload {
  id: string;
  tool: string;
  display_name: string;
  input: unknown;
}

export interface ToolDonePayload {
  id: string;
  tool: string;
  display_name: string;
  summary: string;
  is_error: boolean;
  // Structured tool output. Write tools using the confirmation round-trip
  // (SC-2157) carry the `ConfirmationRequest` payload here on the first
  // invocation; read tools carry their `model_dump()` payload. Parity with
  // ChatToolResultBlock.content from the persisted history.
  content?: Record<string, unknown> | string;
}

export type DoneUsage = Record<string, unknown>;

export interface StreamHandlers {
  onDelta: (text: string) => void;
  onToolStart: (payload: ToolStartPayload) => void;
  onToolDone: (payload: ToolDonePayload) => void;
  onDone: (usage: DoneUsage) => void;
  onError: (message: string) => void;
}

export function getAgentChatSession(
  catalogId: string,
): Promise<ChatSessionResponse> {
  return api.get<ChatSessionResponse>(`/catalogs/${catalogId}/chat`);
}

export interface CreateChatSessionResult {
  session: ChatSessionResponse;
  /** True when the BE inserted a fresh session (HTTP 201). False when it
   *  returned an existing empty session unchanged (HTTP 200) — the BE's
   *  rapid-click idempotency guard: if the active session has no turns yet,
   *  clicking "New chat" again reuses it instead of creating a duplicate.
   *  Drives whether the FE fires the expensive `?refresh=true` briefing regen
   *  (skip it on reuse — nothing changed, and the narrative LLM runs
   *  unconditionally on that path server-side). */
  created: boolean;
}

// Creates a new ChatSession (SC-2250 BE / SC-2251 FE). The response body is
// `{ id, created_at, updated_at, turns: [] }` in both cases; the HTTP status
// is the only signal that distinguishes a genuinely new session (201) from an
// idempotent reuse of an existing empty one (200). The next
// `getAgentChatSession` resolves to this row (BE's "active = most recent").
export async function createChatSession(
  catalogId: string,
): Promise<CreateChatSessionResult> {
  const { data, status } = await api.postWithStatus<ChatSessionResponse>(
    `/catalogs/${catalogId}/chat-sessions`,
  );
  return { session: data, created: status === 201 };
}

/**
 * POSTs a user message and consumes the SSE response stream, dispatching
 * decoded events through `handlers`. Resolves when the stream ends; rejects
 * only on network/transport failure or on AbortError. Protocol-level errors
 * (the `error` event) surface through `onError` and resolve normally.
 *
 * `canvasContext` (SC-2853) is the optional ambient workspace snapshot sent
 * beside the message so the agent knows what the user is looking at. The BE is
 * fully lenient about it — absent or malformed context can never 422 a message.
 *
 * Wire contract: SC-1612 frontend-brief.md.
 */
export async function streamAgentChat(
  catalogId: string,
  content: string,
  handlers: StreamHandlers,
  signal?: AbortSignal,
  canvasContext?: CanvasContextSnapshot,
): Promise<void> {
  const res = await requestStream(`/catalogs/${catalogId}/chat/messages`, {
    method: 'POST',
    body: JSON.stringify(
      canvasContext ? { content, canvas_context: canvasContext } : { content },
    ),
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    signal,
  });

  if (!res.body) {
    handlers.onError('Stream response had no body');
    return;
  }

  // Wrap handlers so we can tell whether a terminal event (done OR error) was
  // observed before the underlying stream closed. Without this guard, a BE
  // crash / proxy truncation / clean connection close mid-stream would leave
  // the caller's state machine stuck at "streaming" forever.
  let sawTerminal = false;
  const tracked: StreamHandlers = {
    onDelta: handlers.onDelta,
    onToolStart: handlers.onToolStart,
    onToolDone: handlers.onToolDone,
    onDone: (usage) => {
      sawTerminal = true;
      handlers.onDone(usage);
    },
    onError: (message) => {
      sawTerminal = true;
      handlers.onError(message);
    },
  };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      buffer = drainEvents(buffer, tracked);
    }
    // Flush any final event without trailing blank line.
    buffer += decoder.decode();
    drainEvents(buffer + '\n\n', tracked);
  } finally {
    reader.releaseLock();
  }

  if (!sawTerminal) {
    handlers.onError('Stream ended unexpectedly');
  }
}

// SSE events are separated by a blank line. Within an event, `data:` lines are
// concatenated. We discriminate event types by JSON shape rather than the SSE
// `event:` field (the BE doesn't emit one).
function drainEvents(buffer: string, handlers: StreamHandlers): string {
  let remaining = buffer;
  while (true) {
    const sep = remaining.indexOf('\n\n');
    if (sep === -1) return remaining;
    const rawEvent = remaining.slice(0, sep);
    remaining = remaining.slice(sep + 2);
    const payload = parseEventPayload(rawEvent);
    if (payload !== null) dispatch(payload, handlers);
  }
}

function parseEventPayload(rawEvent: string): unknown | null {
  const dataLines: string[] = [];
  for (const line of rawEvent.split('\n')) {
    if (line.startsWith('data:')) {
      // Per SSE spec a single leading space after the colon is stripped.
      const slice = line.slice(5);
      dataLines.push(slice.startsWith(' ') ? slice.slice(1) : slice);
    }
    // Ignore comments (lines starting with ':') and other field lines (`event:`, `id:`, `retry:`).
  }
  if (dataLines.length === 0) return null;
  const joined = dataLines.join('\n');
  try {
    return JSON.parse(joined);
  } catch {
    return null;
  }
}

function dispatch(payload: unknown, handlers: StreamHandlers): void {
  if (!payload || typeof payload !== 'object') return;
  const p = payload as Record<string, unknown>;

  // Terminal error event — `done: true` AND `error: string`. Check before
  // `done` since the error event carries both fields.
  if (typeof p.error === 'string') {
    handlers.onError(p.error);
    return;
  }
  if (p.done === true) {
    const usage =
      p.usage && typeof p.usage === 'object'
        ? (p.usage as DoneUsage)
        : ({} as DoneUsage);
    handlers.onDone(usage);
    return;
  }
  if (typeof p.delta === 'string') {
    handlers.onDelta(p.delta);
    return;
  }
  if (p.event === 'tool_use_start') {
    const tool = String(p.tool ?? '');
    handlers.onToolStart({
      id: String(p.id ?? ''),
      tool,
      display_name: typeof p.display_name === 'string' ? p.display_name : tool,
      input: p.input,
    });
    return;
  }
  if (p.event === 'tool_use_done') {
    const tool = String(p.tool ?? '');
    handlers.onToolDone({
      id: String(p.id ?? ''),
      tool,
      display_name: typeof p.display_name === 'string' ? p.display_name : tool,
      summary: String(p.summary ?? ''),
      is_error: Boolean(p.is_error),
      // BE may emit `content` as an object (structured tool output) or string;
      // forward as-is and let downstream type-guards discriminate.
      content:
        typeof p.content === 'object' && p.content !== null
          ? (p.content as Record<string, unknown>)
          : typeof p.content === 'string'
            ? p.content
            : undefined,
    });
    return;
  }
  // Unknown event shape: drop it. Forward-compat with BE adding new events.
}
